import React, { ReactNode } from 'react';
import { Space } from 'antd';
import AppButton from '@nxsys/components/AppButton';
import { StyledModal, StyledTitle, StyledChildren } from './index.styled';
import { createRoot, Root } from 'react-dom/client';
import { AppIconType, Icons } from '@nxsys/types/icons';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import {
  IconContainer,
  WrapperIconByType,
} from '@nxsys/components/AppConfirmationModal/index.styled';

type AppConfirmationModalProps = {
  title?: string | ReactNode;
  open: boolean;
  icon: ReactNode;
  children?: ReactNode;
  paragraph?: string | ReactNode;
  okText?: string;
  cancelText?: string;
  onDeny: () => void;
  onConfirm: () => void | Promise<void>;
  loading?: boolean;
  showFooterButton?: boolean;
};

const AppConfirmationModal: React.FC<AppConfirmationModalProps> = ({
  open,
  icon,
  title,
  children,
  okText = 'Confirm',
  cancelText = 'Cancel',
  paragraph = 'Are you sure you want to confirm?',
  onDeny,
  onConfirm,
  loading,
  showFooterButton = true,
}) => {
  return (
    <StyledModal
      title={null}
      footer={null}
      open={open}
      width={400}
      okText={okText}
      cancelText={cancelText}
      // onOk={onConfirm}
      onCancel={onDeny}
      zIndex={1100}
    >
      {icon}
      {title ? <StyledTitle>{title}</StyledTitle> : null}
      {paragraph}
      {children ? <StyledChildren>{children}</StyledChildren> : null}
      {showFooterButton && (
        <Space className="modal-footer" size={12}>
          <AppButton
            name={cancelText ?? 'Cancel'}
            variant="outlined"
            onClick={onDeny}
            disabled={loading}
          />
          <AppButton
            name={okText ?? 'Confirm'}
            onClick={onConfirm}
            loading={loading}
            disabled={loading}
          />
        </Space>
      )}
    </StyledModal>
  );
};

export const AppConfirmation = {
  open: ({
    title = '',
    paragraph = '',
    onConfirm = () => {},
    icon,
    type = 'warning',
    showFooterButton = true,
    ...rest
  }: Partial<AppConfirmationModalProps> & {
    type?: 'warning' | 'success' | 'error';
    icon?: AppIconType;
  }) => {
    const container = document.createElement('div');
    document.body.appendChild(container);

    let root: Root | null = createRoot(container);

    const cleanup = () => {
      if (root) {
        root.unmount();
        root = null;
      }
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
    };

    const handleDeny = () => {
      cleanup();
    };

    const Icon = icon ? Icons[icon as AppIconType] : null;
    const iconColor =
      type === 'warning'
        ? defaultTheme.theme.palette.orange[700]
        : type === 'success'
          ? defaultTheme.theme.palette.green[500]
          : defaultTheme.theme.palette.red[500];

    const renderModal = (loading: boolean) => {
      root?.render(
        <AppConfirmationModal
          open={true}
          onDeny={handleDeny}
          onConfirm={handleConfirm}
          title={title}
          paragraph={paragraph}
          loading={loading}
          icon={
            Icon ? (
              <IconContainer type={type}>
                <WrapperIconByType type={type}>
                  <Icon color={iconColor} />
                </WrapperIconByType>
              </IconContainer>
            ) : null
          }
          showFooterButton={showFooterButton}
          {...rest}
        />,
      );
    };

    const handleConfirm = async () => {
      try {
        // Keep the dialog open with a loading state until the async action resolves
        renderModal(true);
        await onConfirm();
      } finally {
        cleanup();
      }
    };

    renderModal(false);
  },
};

export default AppConfirmationModal;
